import * as React from 'react'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Skeleton } from '@/components/ui/skeleton'
import { Search, MessageSquare } from 'lucide-react'
import { useWAConversations } from '@/hooks/useWhatsApp'
import { ConversationItem } from './conversation-item'
import type { WAConversation, WAConversationStatus } from '@/lib/api-client'

type ConversationTab = 'all' | 'replies' | 'unread' | 'archived'

interface ConversationListProps {
  branchId?: number
  statusFilter: WAConversationStatus | 'all'
  onStatusFilterChange: (status: WAConversationStatus | 'all') => void
  searchQuery: string
  onSearchChange: (query: string) => void
  selectedId?: number
  onSelect: (conversation: WAConversation) => void
  stats?: { total: number; unread: number }
}

export function ConversationList({
  branchId,
  statusFilter,
  onStatusFilterChange,
  searchQuery,
  onSearchChange,
  selectedId,
  onSelect,
  stats,
}: ConversationListProps) {
  const [debouncedSearch, setDebouncedSearch] = React.useState(searchQuery)
  const [activeTab, setActiveTab] = React.useState<ConversationTab>('all')

  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const { data, isPending, isError } = useWAConversations({
    branchId,
    status: activeTab === 'archived' ? 'archived' : 'all',
    unreadOnly: activeTab === 'unread',
    hasReplies: activeTab === 'replies',
    search: debouncedSearch || undefined,
    limit: 50,
  })

  const conversations = data?.data ?? []

  return (
    <div className="flex flex-col h-full">
      {/* Title */}
      <div className="flex-shrink-0 px-4 pt-4 pb-3 bg-white">
        <h1 className="text-lg font-semibold text-[#111b21]">WhatsApp</h1>
        <p className="text-[13px] text-[#667781]">
          {stats
            ? `${stats.total} conversations · ${stats.unread} unread`
            : 'Loading...'}
        </p>
      </div>

      {/* Toolbar */}
      <div className="flex-shrink-0 px-3 pb-3 pt-0.5 space-y-2.5 border-b border-[#e9edef] bg-white">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
          <Input
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9 h-9 text-[13px] bg-[#f0f2f5] border-0 rounded-lg text-[#111b21] placeholder:text-[#667781]/60"
          />
        </div>

        {/* Filter tabs */}
        <div className="flex items-center gap-1.5">
          {([
            { value: 'all' as const, label: 'All' },
            { value: 'replies' as const, label: 'Replies' },
            { value: 'unread' as const, label: 'Unread', count: stats?.unread },
            { value: 'archived' as const, label: 'Archived' },
          ]).map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => setActiveTab(tab.value)}
              className={cn(
                'px-3 py-1.5 rounded-full text-xs font-medium transition-colors duration-150',
                activeTab === tab.value
                  ? 'bg-[#00a884]/10 text-[#00a884]'
                  : 'bg-[#f0f2f5] text-[#667781] hover:bg-[#e9edef] hover:text-[#111b21]'
              )}
            >
              {tab.label}
              {tab.count ? ` (${tab.count})` : ''}
            </button>
          ))}
        </div>
      </div>

      {/* Conversation List */}
      <ScrollArea className="flex-1 bg-white">
        <div className="w-0 min-w-full overflow-hidden">
          {isPending ? (
            Array.from({ length: 8 }).map((_, i) => (
              <ConversationSkeleton key={i} />
            ))
          ) : isError ? (
            <div className="p-8 text-center">
              <p className="text-sm text-destructive">Failed to load conversations</p>
            </div>
          ) : conversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-8">
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
                <MessageSquare className="w-5 h-5 text-text-muted" />
              </div>
              <p className="text-sm text-text-muted">No conversations found</p>
            </div>
          ) : (
            conversations.map((conversation) => (
              <ConversationItem
                key={conversation.id}
                conversation={conversation}
                isSelected={selectedId === conversation.id}
                onClick={() => onSelect(conversation)}
              />
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  )
}

function ConversationSkeleton() {
  return (
    <div className="flex items-start gap-3 px-3 py-3.5">
      <Skeleton className="w-12 h-12 rounded-full flex-shrink-0" />
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex items-center justify-between">
          <Skeleton className="h-3.5 w-28" />
          <Skeleton className="h-3 w-10" />
        </div>
        <Skeleton className="h-3 w-3/4" />
      </div>
    </div>
  )
}
